import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { 
  Search, 
  Download, 
  Loader2,
  ChevronUp,
  ChevronDown,
  Calendar,
  User,
  Trash2,
  CheckSquare,
  LayoutList,
  Columns3,
  Building
} from 'lucide-react';
import { toast } from 'sonner';
import KanbanBoard from '../components/KanbanBoard';

const Projects = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, isSubmitter } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [processFilter, setProcessFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [processes, setProcesses] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'list');
  const [kanbanGroupBy, setKanbanGroupBy] = useState('current_status');
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, [statusFilter, ownerFilter, typeFilter]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (ownerFilter && ownerFilter !== 'all') params.append('owner_id', ownerFilter);
      if (typeFilter && typeFilter !== 'all') params.append('project_type', typeFilter);
      
      const response = await api.get(`/projects?${params.toString()}`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const [userRes, procRes] = await Promise.all([
        api.get('/users'),
        api.get('/processes'),
      ]);
      setUsers(userRes.data);
      setProcesses(procRes.data.filter(p => p.level >= 3));
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/export/projects');
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredProjects = projects
    .filter(project => {
      // Submitters only see their department's projects
      if (isSubmitter() && project.department !== user?.department) return false;
      if (processFilter && processFilter !== 'all' && project.linked_process_id !== processFilter) return false;
      if (!searchQuery) return true;
      return (
        project.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.owner_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Helper to get annual benefit from financial_tracking
      const getExpectedBenefit = (proj) => {
        const ft = proj.financial_tracking;
        if (ft?.savings) {
          return (ft.savings.one_off_expected || 0) + ((ft.savings.ongoing_expected || 0) * 12);
        }
        return proj.value_metrics?.annualised_benefit || 0;
      };
      
      const getRealisedBenefit = (proj) => {
        const ft = proj.financial_tracking;
        if (ft?.savings) {
          return (ft.savings.one_off_actual || 0) + ((ft.savings.ongoing_actual || 0) * 12);
        }
        return proj.value_metrics?.final_value || 0;
      };
      
      if (sortField === 'expected_benefit') {
        aVal = getExpectedBenefit(a);
        bVal = getExpectedBenefit(b);
      }
      if (sortField === 'realised_benefit') {
        aVal = getRealisedBenefit(a);
        bVal = getRealisedBenefit(b);
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? (aVal || '').localeCompare(bVal || '') 
          : (bVal || '').localeCompare(aVal || '');
      }
      
      return sortDirection === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
    });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 inline ml-1" />
      : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  const getProgressValue = (project) => {
    const statusProgress = {
      'Not Started': 0,
      'In Progress': 50,
      'Blocked': 50,
      'Completed': 100,
      'Cancelled': 0
    };
    return statusProgress[project.current_status] || 0;
  };

  const clearFilters = () => {
    setStatusFilter('');
    setOwnerFilter('');
    setTypeFilter('');
    setProcessFilter('');
    setSearchQuery('');
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProjects.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredProjects.map(p => p.id)));
  };

  const handleBulkAction = async (action, targetStatus) => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${action} ${selectedIds.size} project(s)?`)) return;
    try {
      const res = await api.post('/projects/bulk-action', {
        project_ids: [...selectedIds],
        action,
        target_status: targetStatus || null,
      });
      toast.success(`${res.data.success} updated${res.data.failed ? `, ${res.data.failed} failed` : ''}`);
      setSelectedIds(new Set());
      fetchProjects();
    } catch (err) {
      toast.error('Bulk action failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Projects</h1>
          <p className="text-slate-500 mt-1">Track and manage improvement projects</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex border rounded-lg overflow-hidden" data-testid="project-view-toggle">
            <button onClick={() => { setViewMode('list'); setSearchParams(prev => { prev.delete('view'); return prev; }); }} className={`px-3 py-2 text-sm ${viewMode === 'list' ? 'bg-[#235894] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
              <LayoutList className="w-4 h-4" />
            </button>
            <button onClick={() => { setViewMode('kanban'); setSearchParams(prev => { prev.set('view', 'kanban'); return prev; }); }} className={`px-3 py-2 text-sm ${viewMode === 'kanban' ? 'bg-[#235894] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
              <Columns3 className="w-4 h-4" />
            </button>
          </div>
          <Button 
            variant="outline" 
            onClick={handleExport}
            data-testid="export-projects-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-projects-input"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="project-status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="w-40" data-testid="owner-filter">
                  <SelectValue placeholder="All Owners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Owners</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40" data-testid="project-type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Quick Win">Quick Win</SelectItem>
                  <SelectItem value="Tech Project">Tech Project</SelectItem>
                  <SelectItem value="DMAIC">DMAIC</SelectItem>
                </SelectContent>
              </Select>

              <Select value={processFilter} onValueChange={setProcessFilter}>
                <SelectTrigger className="w-48" data-testid="project-process-filter">
                  <SelectValue placeholder="All Processes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Processes</SelectItem>
                  {processes.map(p => (
                    <SelectItem key={p.id} value={p.id}>L{p.level} {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(statusFilter || ownerFilter || typeFilter || processFilter || searchQuery) && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  data-testid="clear-project-filters-btn"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && isAdmin() && viewMode === 'list' && (
        <div className="flex items-center gap-3 px-4 py-2 bg-[#235894] text-white rounded-lg" data-testid="project-bulk-bar">
          <CheckSquare className="w-4 h-4" />
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex gap-2 ml-4">
            <Button size="sm" variant="secondary" onClick={() => handleBulkAction('change_status', 'In Progress')}>Start</Button>
            <Button size="sm" variant="secondary" onClick={() => handleBulkAction('change_status', 'Completed')}>Complete</Button>
            <Button size="sm" variant="secondary" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => handleBulkAction('delete')}>
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="ml-auto text-white/80 hover:text-white" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && !loading && (
        <KanbanBoard
          items={filteredProjects}
          groupByField={kanbanGroupBy}
          groupByOptions={[
            { value: 'current_status', label: 'Status' },
            { value: 'project_type', label: 'Type' },
            { value: 'owner_name', label: 'Owner' },
          ]}
          columnOrder={
            kanbanGroupBy === 'current_status' ? ['Not Started', 'In Progress', 'Blocked', 'Completed', 'Cancelled'] :
            kanbanGroupBy === 'project_type' ? ['Quick Win', 'Tech Project', 'DMAIC'] :
            [...new Set(filteredProjects.map(p => p.owner_name || 'Unknown'))]
          }
          onGroupByChange={setKanbanGroupBy}
          onItemDrop={kanbanGroupBy === 'current_status' ? async (id, newStatus) => {
            try {
              await api.put(`/projects/${id}`, { current_status: newStatus });
              fetchProjects();
              toast.success(`Moved to ${newStatus}`);
            } catch (err) { toast.error('Failed to update status'); }
          } : null}
          cardConfig={{
            title: 'project_name',
            badges: (item) => [item.project_type, item.department],
            meta: (item) => [
              { icon: <User className="w-3 h-3" />, text: item.owner_name },
              item.target_end_date ? { icon: <Calendar className="w-3 h-3" />, text: item.target_end_date } : null,
            ],
            link: (item) => `/projects/${item.id}`,
          }}
          entityType="project"
        />
      )}

      {/* Table View */}
      {viewMode === 'list' && (
      <>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <p className="text-lg">No projects found</p>
              <p className="text-sm">Convert approved ideas to projects to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {isAdmin() && (
                      <TableHead className="w-10">
                        <input type="checkbox" checked={selectedIds.size === filteredProjects.length && filteredProjects.length > 0} onChange={toggleSelectAll} className="rounded" />
                      </TableHead>
                    )}
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('project_name')}
                    >
                      Project <SortIcon field="project_name" />
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('expected_benefit')}
                    >
                      Expected <SortIcon field="expected_benefit" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('realised_benefit')}
                    >
                      Realised <SortIcon field="realised_benefit" />
                    </TableHead>
                    <TableHead>Timeline</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjects.map((project) => (
                    <TableRow 
                      key={project.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/projects/${project.id}`)}
                      data-testid={`project-row-${project.id}`}
                    >
                      {isAdmin() && (
                        <TableCell onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(project.id)} onChange={() => toggleSelect(project.id)} className="rounded" />
                        </TableCell>
                      )}
                      <TableCell className="font-medium max-w-xs">
                        <div>
                          <p className="truncate">{project.project_name}</p>
                          <p className="text-xs text-slate-400 truncate">{project.idea_title}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={project.project_type} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span>{project.owner_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={project.current_status} />
                      </TableCell>
                      <TableCell>
                        <div className="w-24">
                          <Progress value={getProgressValue(project)} className="h-2" />
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const ft = project.financial_tracking;
                          if (ft?.savings) {
                            const annual = (ft.savings.one_off_expected || 0) + ((ft.savings.ongoing_expected || 0) * 12);
                            return annual > 0 ? `£${annual.toLocaleString()}` : '-';
                          }
                          return project.value_metrics?.annualised_benefit 
                            ? `£${project.value_metrics.annualised_benefit.toLocaleString()}`
                            : '-';
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const ft = project.financial_tracking;
                          if (ft?.savings) {
                            const annual = (ft.savings.one_off_actual || 0) + ((ft.savings.ongoing_actual || 0) * 12);
                            return annual > 0 ? `£${annual.toLocaleString()}` : '-';
                          }
                          return project.value_metrics?.final_value 
                            ? `£${project.value_metrics.final_value.toLocaleString()}`
                            : '-';
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" />
                          {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}
                          {' → '}
                          {project.target_end_date ? new Date(project.target_end_date).toLocaleDateString() : '-'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-sm text-slate-500">
        Showing {filteredProjects.length} of {projects.length} projects
      </p>
      </>
      )}
    </div>
  );
};

export default Projects;
